import { usePrevious } from 'adapter/in/common/hooks/usePrevious';
import React, { ReactNode, CSSProperties, createContext, useContext, useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnimate } from 'react-simple-animate';

export const alertHeight = '43px';

export enum ALERT_ACTION_TYPE {
    WARNING = 'warning',
    SUCCESS = 'success',
    HTML_SUCCESS = 'success',
    ERROR = 'error',
    RESET = 'reset',
    STYLE = 'style',
}

export type AlertDispatchContextType = {
    setSuccessAlert: (message: string) => void;
    setHTMLSuccessAlert: (message: ReactNode) => void;
    setErrorAlert: (message: string) => void;
    setWarningAlert: (message: string, resetStyle?: boolean) => void;
    resetAlert: () => void;
    setStyle: (style: CSSProperties) => void;
};

const initialState = {
    type: ALERT_ACTION_TYPE.SUCCESS,
    message: '' as AlertMessageType,
    show: false,
    style: { display: 'none' } as CSSProperties,
};

export type AlertMessageType = ReactNode;

export type AlertType = {
    type: ALERT_ACTION_TYPE;
    message: AlertMessageType;
    show: boolean;
    style: CSSProperties;
};
export const useCaseAlertViewReducer = (state: AlertType, action: AlertPayloadType): AlertType => {
    const { type, payload } = action;
    switch (type) {
        case ALERT_ACTION_TYPE.SUCCESS:
            return {
                ...state,
                type: ALERT_ACTION_TYPE.SUCCESS,
                message: payload as AlertMessageType,
                show: true,
            };
        case ALERT_ACTION_TYPE.HTML_SUCCESS:
            return {
                ...state,
                type: ALERT_ACTION_TYPE.SUCCESS,
                message: payload as AlertMessageType,
                show: true,
            };
        case ALERT_ACTION_TYPE.ERROR:
            return {
                ...state,
                type: ALERT_ACTION_TYPE.ERROR,
                message: payload as AlertMessageType,
                show: true,
            };
        case ALERT_ACTION_TYPE.WARNING:
            return {
                ...state,
                type: ALERT_ACTION_TYPE.WARNING,
                message: payload as AlertMessageType,
                show: true,
            };
        case ALERT_ACTION_TYPE.STYLE:
            return {
                ...state,
                style: payload as CSSProperties,
            };
        case ALERT_ACTION_TYPE.RESET:
        default:
            return {
                ...state,
                message: '',
                show: false,
            };
    }
};

export const AlertDispatchContext = createContext({} as AlertDispatchContextType);
export const AlertViewStateContext = createContext(initialState);
type AlertPayloadType = {
    type: ALERT_ACTION_TYPE;
    payload: CSSProperties | AlertMessageType;
};
export const dispatchAlertToaster = (): AlertDispatchContextType => useContext(AlertDispatchContext);
export const useAlertToaster = (): AlertType => useContext(AlertViewStateContext);

export type AlertTypeProviderType = {
    children: ReactNode;
};

export const AlertToasterProvider = (props: AlertTypeProviderType): ReactNode => {
    const { children } = props;
    const [state, dispatch] = useReducer(useCaseAlertViewReducer, initialState);
    const location = useLocation();
    const prevLocationPathName = usePrevious(location.pathname);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | undefined>(undefined);

    const { play, style } = useAnimate({
        duration: 0.2,
        start: { transform: `translateY(-${alertHeight})` },
        end: { transform: 'translateY(0)' },
    });

    const setSuccessAlert = (message: string): void => {
        handleDelayedAlertClosing();

        setStyle(style);
        dispatch({ type: ALERT_ACTION_TYPE.SUCCESS, payload: message });
    };

    const setHTMLSuccessAlert = (message: ReactNode): void => {
        setStyle(style);
        dispatch({ type: ALERT_ACTION_TYPE.HTML_SUCCESS, payload: message });
    };
    const setErrorAlert = (message: string): void => {
        handleDelayedAlertClosing();
        setStyle(style);
        dispatch({ type: ALERT_ACTION_TYPE.ERROR, payload: message });
    };
    const setWarningAlert = (message: string, resetStyle = true): void => {
        if (resetStyle) setStyle(style);
        dispatch({ type: ALERT_ACTION_TYPE.WARNING, payload: message });
    };

    const handleDelayedAlertClosing = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        const newTimeout = setTimeout(() => {
            resetAlert();
            setTimeoutId(undefined);
        }, 3000);
        setTimeoutId(newTimeout);
    };
    const resetAlert = (): void => dispatch({ type: ALERT_ACTION_TYPE.RESET, payload: '' });
    const setStyle = (styleArg: CSSProperties): void => dispatch({ type: ALERT_ACTION_TYPE.STYLE, payload: styleArg });

    useEffect(() => {
        setStyle(style);
    }, [style]);

    useEffect(() => {
        if (state.show) {
            play(true);
        } else {
            play(false);
        }
    }, [state.show]);

    useEffect(() => {
        if (location.pathname !== prevLocationPathName) {
            resetAlert();
        }
    }, [location]);

    return (
        <AlertViewStateContext.Provider value={state}>
            <AlertDispatchContext.Provider
                value={{ setSuccessAlert, setErrorAlert, setWarningAlert, resetAlert, setStyle, setHTMLSuccessAlert }}
            >
                {children}
            </AlertDispatchContext.Provider>
        </AlertViewStateContext.Provider>
    );
};
